/**
 * CustomPulldown generates a pulldown based on the options passed in
 *
 *      Xoom 'Module' Format.
 *      Documentation: http://confluence/display/Eng/JavaScript+Module+Framework
 */
/* jslint es6 */

import $ from 'zepto';
import { onDocumentReady } from '../../../main';
import {
  $$$,
  registerDomObjects,
  random,
} from '../../../utils/legacy';

import { isVisible } from '../../../utils';
import helpers, { isElementInitialized, safeCallbackCall } from './customPulldownHelper';

const pulldowns = {};

/**
 * Initializes pulldown
 * @method init
 * @param {Int} eltId element ID
 * @param {Boolean} isFullWidth Flag to verify if display is full width
 *
*/
function init(eltId, isFullWidth) {
  this.pickerId = eltId;
  this.$originalHtmlSelectElement = $$$(eltId);
  this.savedChoice = this.$originalHtmlSelectElement.val();
  this.highlightValue = this.savedChoice;
  this.customPulldownContentElementId = `${eltId}custom-pulldown`;
  const localObject = this.getLocalObject();

  this.originalHtmlSelectElementWidth = localObject.originalHtmlSelectDomElement.offsetWidth;
  this.setCallBacks(localObject.originalHtmlSelectDomElement);
  this.displayStyle = this.getDisplayStyle(isFullWidth);
  const customPulldownFieldDimensions = this.getCustomPullDownDimensions(isFullWidth, {}, localObject.height);

  this.$originalHtmlSelectElement.before(this.createPulldown(localObject.options, localObject.selectClass));
  this.$customPulldownFieldElement = $$$(`${eltId}-fld`); // set a pointer to the custom pulldown field element
  this.$pickerElt = $$$(`${this.pickerId}-sel`);
  this.$inputElt = $$$(`${eltId}-inp`);
  this.customPulldownContentWidth = this.getPulldownContentWidth();
  const className = this.getClassName(localObject.originalHtmlSelectDomElement, 'countryPulldown');
  this.setSelectedItemProps(className);
  this.uiElementScope = this.getUIElementScope();

  // bind a click to the custom pulldown field element also set width and height correctly
  this.$customPulldownFieldElement.css(customPulldownFieldDimensions).bind('click', () => {
    this.adjustDropDownWidthBeforeShowing();
    this.togglePulldown();
    this.inputEltSoftFocus = false; // so showPulldown() will show drop-down
  });

  $('.customSelect', this.$customPulldownFieldElement).css(customPulldownFieldDimensions);
  this.dropDown = this.getDropdown(className);

  // set a reference to the element which displays the currently selected item
  this.$selectedItemDisplayElement = $('.custom-select-value-display', this.$customPulldownFieldElement);

  this.setEdgeAttributes();
  this.setBindPropsPickerElt();
  this.setBindPropsInputElt();
  this.updateVisualsWithSelItem();
}

/**
 * Initializes variables and set into the same scope all variables and functions used for pulldown.
 * @method initPullDown
 *
*/
function initPullDown() {
  // eslint-disable-next-line prefer-object-spread
  return Object.assign({}, helpers, {
    originalHtmlSelectElementWidth: undefined,
    updateVisualCallback: undefined,
    afterSelectionCallback: undefined,
    afterSelectionCallbackName: undefined,
    savedChoice: undefined,
    createRowCallbackFunction: undefined,
    inputEltSoftFocus: undefined,
    arrowPressedimeout: undefined,
    arrowPressed: 0,
    uiElementScope: undefined,
    isFullWidthField: undefined,
    displayStyle: 'inline-block',
    pickerId: undefined,
    $pickerElt: undefined,
    $inputElt: undefined,
    $originalHtmlSelectElement: undefined,
    highlightValue: undefined,
    dropDown: undefined,
    $customPulldownFieldElement: undefined,
    $selectedItemDisplayElement: undefined,
    customPulldownContentElementId: undefined,
    customPulldownContentWidth: undefined,
    showPulldownTimeout: undefined,
    clearInputTimeout: undefined, // timeout to clear the hidden input for type selection
    init,
    destroy() {
      this.unbindProps();
    },
    hide() {
      this.dropDown.hide();
    },
    show() {
      this.dropDown.show();
    },
    refresh() {
      this.updateOptionsList();
    },
    focus() {
      this.softFocus();
    },
    update() {
      this.dropDown.hide();
    },
  });
}

/**
 * Creates public methods as initializeWidget, add and get for custom pulldown
 * @method initCustomPulldown
 *
*/
export function initCustomPulldown() {
  /**
   * Creates method and register it as dom object
   * @method addPulldown
   * @param {Int} elementId Element ID
   *
 */
  function addPulldown(elementId) {
    let pulldown;
    const $element = $$$(elementId);

    if (isElementInitialized($element, elementId)) {
      return pulldowns[elementId];
    }
    if (isVisible($element) && !$element.hasClass('has-custom-select') && !$element.hasClass('has-custom-pulldown')) {
      // add flag to element that says it's initializing to prevent being initialized multiple times
      $element.attr('data-custom-pulldown-initializing', 'true');
      pulldown = initPullDown();
      pulldown.setUIElementScope($element);
      pulldown.isFullWidthField = pulldown.getIsFullWidthFieldValue($element);
      pulldowns[elementId] = pulldown;
      pulldown.init(elementId, pulldown.isFullWidthField);
      registerDomObjects(`countrySelect-${elementId}`, pulldown, pulldown.uiElementScope);
    }
    return pulldown;
  }

  /**
   * Gets method using element id
   * @method getPulldown
   * @param {Int} elementId Element ID
   *
 */
  function getPulldown(elementId) {
    return pulldowns[elementId];
  }

  /**
   * Initializes custom pulldown with random ids if they not exists
   * @method initializeWidget
   * @param {domElement} contextSelector element where custom select are placed
   * @param {callback} callback callback
   *
 */
  function initializeWidget(contextSelector, callback) {
    const $customPulldownSelects = $('select.custom-pulldown, select.language-pulldown', contextSelector || $('body'));
    if ($customPulldownSelects.length) {
      $customPulldownSelects.each(function customPulldownSelected() {
        let { id } = this;
        if (!id) { // add an id to the element if one doesn't exist.
          id = `random-gen-${random()}`;
          this.id = id;
        }
        addPulldown(id);
      });
    }
    safeCallbackCall(callback);
  }

  return {
    initializeWidget,
    add: addPulldown,
    get(id) {
      return getPulldown(id);
    },
  };
}

// TODO: Add context implementation
onDocumentReady('CustomPulldown', () => {
  initCustomPulldown().initializeWidget();
});

export default {
  initCustomPulldown,
};

registerDomObjects('CustomPulldown', initCustomPulldown);
